import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "eventSelect",
    "customerDisplay",
    "modeSelectBtn",
    "modeCreateBtn",
    "eventModeField",
    "selectEventSection",
    "createEventSection",
    "itemsContainer",
    "itemTemplate",
    "installmentsContainer",
    "installmentTemplate"
  ]

  connect() {
    this.#setAgendamentoMode(this.eventModeFieldTarget.value)
    this.#updateCustomer()
    this.#initializeFirstInstallment()
  }

  selectExistingMode() {
    this.#setAgendamentoMode("select")
  }

  createNewMode() {
    this.#setAgendamentoMode("create")
  }

  eventChanged() {
    this.#updateCustomer()
  }

  addItem() {
    const content = this.itemTemplateTarget.innerHTML.replace(/NEW_RECORD/g, Date.now())
    this.itemsContainerTarget.insertAdjacentHTML("beforeend", content)
  }

  addInstallment() {
    const content = this.installmentTemplateTarget.innerHTML.replace(/NEW_RECORD/g, Date.now())
    this.installmentsContainerTarget.insertAdjacentHTML("beforeend", content)

    const dateInputs = this.#installmentDateInputs()
    const newDateInput = dateInputs[dateInputs.length - 1]
    if (newDateInput) newDateInput.value = this.#nextInstallmentDate()

    this.#redistributeAmounts()
  }

  removeItem(event) {
    event.target.closest("[data-item-row]").remove()
    this.#redistributeAmounts()
  }

  removeInstallment(event) {
    event.target.closest("[data-installment-row]").remove()
    this.#redistributeAmounts()
  }

  itemChanged(event) {
    const row = event.target.closest("[data-item-row]")
    const unitPriceInput = row?.querySelector("[data-order-form-unit-price]")
    const amount = event.target.selectedOptions[0]?.dataset.amount

    if (unitPriceInput) this.#setMonetaryValue(unitPriceInput, amount ? parseFloat(amount) : '')
    this.#redistributeAmounts()
  }

  recalculate() {
    this.#redistributeAmounts()
  }

  // Fired only for user-driven edits (mask:accept is suppressed for
  // programmatic setValue calls), so redistribution never re-triggers this.
  // Emptying the field hands the row back to automatic distribution.
  installmentAmountChanged(event) {
    const row = event.target.closest("[data-installment-row]")
    this.#setInstallmentFixed(row, !!event.target.dataset.rawValue)
    this.#redistributeAmounts()
  }

  // Disabling the inactive section's fields (not just hiding them) keeps the
  // pre-filled nested event attributes out of the submit in "select" mode —
  // otherwise Rails builds a duplicate Event on every order update.
  #setAgendamentoMode(mode) {
    const isCreate = mode === "create"

    this.modeSelectBtnTarget.classList.toggle("active", !isCreate)
    this.modeCreateBtnTarget.classList.toggle("active", isCreate)
    this.selectEventSectionTarget.classList.toggle("d-none", isCreate)
    this.createEventSectionTarget.classList.toggle("d-none", !isCreate)

    this.#fieldsIn(this.selectEventSectionTarget).forEach(el => this.#setFieldDisabled(el, isCreate))
    this.#fieldsIn(this.createEventSectionTarget).forEach(el => this.#setFieldDisabled(el, !isCreate))

    this.eventSelectTarget.required = !isCreate
    this.eventModeFieldTarget.value = mode
  }

  #fieldsIn(element) {
    return [...element.querySelectorAll("input, select, textarea")]
  }

  // Tom Select captures the select's disabled state at init and ignores later
  // changes to the property, so its widget must be toggled through its API
  // (which also syncs the underlying select).
  #setFieldDisabled(el, disabled) {
    if (el.tomselect) {
      disabled ? el.tomselect.disable() : el.tomselect.enable()
    } else {
      el.disabled = disabled
    }
  }

  #calculateTotal() {
    let cents = 0
    this.itemsContainerTarget.querySelectorAll("[data-item-row]").forEach(row => {
      const priceInput = row.querySelector("[data-order-form-unit-price]")
      const price = parseFloat(priceInput?.dataset.rawValue ?? priceInput?.value) || 0
      const qty = parseInt(row.querySelector("[data-order-form-quantity]")?.value) || 1
      cents += Math.round(price * qty * 100)
    })
    return cents
  }

  #installmentAmountInputs() {
    return [...this.installmentsContainerTarget.querySelectorAll(
      "[data-installment-row] [data-order-form-installment-amount]"
    )]
  }

  #installmentDateInputs() {
    return [...this.installmentsContainerTarget.querySelectorAll(
      "[data-installment-row] [data-order-form-installment-date]"
    )]
  }

  // Distributes the order total across the installments whose amount the user
  // has not edited by hand. Rows marked fixed keep their value and only shrink
  // the pool shared by the automatic ones.
  #redistributeAmounts() {
    const inputs = this.#installmentAmountInputs()
    const autoInputs = inputs.filter(input => !this.#isFixed(input))
    if (autoInputs.length === 0) return

    const totalCents = this.#calculateTotal()
    if (totalCents === 0) {
      autoInputs.forEach(input => { this.#setMonetaryValue(input, '') })
      return
    }

    const fixedCents = inputs
      .filter(input => this.#isFixed(input))
      .reduce((sum, input) => {
        const value = parseFloat(input.dataset.rawValue ?? input.value) || 0
        return sum + Math.round(value * 100)
      }, 0)

    const remainingCents = Math.max(totalCents - fixedCents, 0)
    const baseCents = Math.floor(remainingCents / autoInputs.length)
    const remainderCents = remainingCents - baseCents * autoInputs.length

    autoInputs.forEach((input, i) => {
      const cents = i === autoInputs.length - 1 ? baseCents + remainderCents : baseCents
      this.#setMonetaryValue(input, cents / 100)
    })
  }

  #isFixed(input) {
    return input.closest("[data-installment-row]")?.dataset.installmentFixed === "true"
  }

  #setInstallmentFixed(row, fixed) {
    if (row) row.dataset.installmentFixed = fixed ? "true" : "false"
  }

  // Sets a numeric value on a monetary input, using the mask controller's
  // setValue() API when available so the display stays formatted.
  // Falls back to direct .value assignment for inputs without a mask
  // (e.g. newly inserted rows whose Stimulus controller hasn't connected yet;
  // the mask controller's connect() will format the value on next tick).
  #setMonetaryValue(input, num) {
    const maskCtrl = this.application.getControllerForElementAndIdentifier(input, 'mask')
    if (maskCtrl) {
      maskCtrl.setValue(num)
    } else {
      input.value = (num === '' || num === null || num === undefined) ? '' : Number(num).toFixed(2)
    }
  }

  #nextInstallmentDate() {
    const filledDates = this.#installmentDateInputs()
      .map(input => input.value)
      .filter(v => v)

    if (filledDates.length === 0) return ""

    const date = new Date(filledDates[filledDates.length - 1] + "T00:00:00")
    date.setDate(date.getDate() + 30)
    return date.toISOString().split("T")[0]
  }

  #initializeFirstInstallment() {
    const dateInputs = this.#installmentDateInputs()
    if (dateInputs.length === 0 || dateInputs[0].value) return

    dateInputs[0].value = new Date().toISOString().split("T")[0]
    this.#redistributeAmounts()
  }

  #updateCustomer() {
    const option = this.eventSelectTarget.selectedOptions[0]
    const name = option?.dataset.customerName
    this.customerDisplayTarget.textContent = name || "—"
  }
}
